import React from "react";

/**
 * @typedef {Object} StratosLoaderDataProps
 * @property {ItemData  &{[key:string]: any}} itemData
 * @property {{darwinDealerId?: string, [key:string]: any}[]} accountList
 */
/** @type {StratosLoaderDataProps} */

/** Invisible `div` containing meta data for Darwin Bookout.
 * Requires the vehicle's VIN, image, and mileage - from corresponding AWG or ES value.
 * @param {StratosLoaderDataProps} props
 * @returns {React.JSX.Element|null}
 */
const StratosLoaderData = ({itemData, accountList=[]}) => {
    if (itemData && 
        (itemData.vin || itemData.vIN) && 
        (checkMileage(itemData.mileage) || checkMileage(itemData.odometer)) &&
        (itemData.imageFullUrls || itemData.mainImage || itemData.hasImage === false)
    ) {
      const {darwinDealerId, multiDealerIds} = findDealerIds(accountList);
      const {vin, vIN, odometer, mileage, imageFullUrls, mainImage=''} = itemData;
      const vehicleImage = imageFullUrls?.length ? imageFullUrls[0] : mainImage;
      return (
        <div 
          style={{width: 0}}
          className="darwinVehicleData"
          data-vin={vin || vIN}
          data-mileage={getMileage([odometer, mileage])}
          data-img={vehicleImage}
          data-darwindealerid={darwinDealerId || ''}
          data-multidealer={multiDealerIds}
          data-price={getSalePrice(itemData)}
        />
      )
    } else {
      return null;
    }
}

/** Invisible `div` containing meta data for Darwin Recommended BI.
 * - requires `accountList` with `darwinDealerId` & _optional_ `multiDealerIds`
 * @param {{accountList: {darwinDealerId: string, [key:string]: any}}} props
 * @returns {React.JSX.Element}
 */
const StratosLoaderDummyData = ({accountList=[]}) => {
  const {darwinDealerId, multiDealerIds} = findDealerIds(accountList);
  const mileage = 30000;
  const price = 10000;
  const vehicleImage = "https://media.chromedata.com/MediaGallery/media/MzM1MDEyXk1lZGlhIEdhbGxlcnk/xAQtKhc31YjT4InSmel8YxYspTnbKZKEInzInRt-Cqhjm3MtpreZc7BIxVlzQeK2CJ_b9aRtVllAFdzvBOxpfC9FqEJfJWtVIl5H9mu3Du_oggKlRddaJiTizAFGNoj9bXTtJR5cnhXp0Yv0ANLCkA/2011BMW005a_640_01.jpg";
  const vin = "1GCWGFBA8C1201433";
  return (
    <div 
      style={{width: 0}}
      className="darwinVehicleDataInit"
      data-vin={vin}
      data-mileage={mileage}
      data-price={price}
      // data-img={vehicleImage}
      data-darwindealerid={darwinDealerId || ''}
      data-multidealer={multiDealerIds}
      id="darwinInjectionStub"
    />
  )
}

/* ---- Local Functions ---- */
/** Finds initial (assumed primary) darwin dealer ID + remaining ID's from given `userprofile.accountList`
 * 
 * @param {{darwinDealerId?: string, [key:string]: any}} accountList 
 * @returns {{darwinDealerId: string, multiDealerIds: string}}
 */
function findDealerIds (accountList=[]) {
  try {
    const darwinDealerId = accountList ? accountList.find(acct => acct.darwinDealerId)?.darwinDealerId : '';
    const multiDealerIds = accountList.filter(acct => acct?.darwinDealerId && acct.darwinDealerId !== darwinDealerId).map(a=>a.darwinDealerId).join(',');
    return {darwinDealerId,multiDealerIds};
  } catch (parseDealerError) {
    console.error(' Error finding darwin dealer ID(s) ', parseDealerError?.message || parseDealerError);
    return {darwinDealerId: '', multiDealerIds: ''};
  }
}

/** Return relevant vehicle price.
 * **Update 2025.04** - No longer used for `data-price`
 * 
 * @param {ItemData &ItemPriceData} itemData 
 */
function getPrice (itemData) {
  const {outrightPrice, highBid, highOffer, nextValidBidAmount, startingBid} = itemData;
  return [
    outrightPrice, nextValidBidAmount, highBid, highOffer, startingBid,
  ].find(val => val) || '';
}

/** Return relevant vehicle price.
 * 
 * @param {ItemData &ItemPriceData} itemData 
 */
function getSalePrice (itemData) {
  const {
    outrightPrice,
  } = itemData;
  return outrightPrice || '';
}

/**
 * @param {number|undefined} miles 
 * @returns 
 */
function checkMileage(miles) {
  return (typeof miles === "number");
}

/**
 * @param {Array<number|undefined>} milesProperties 
 */
function getMileage(milesProperties=[]) {
  return milesProperties.find(mileage => {
    return (
      (typeof mileage === "number" && mileage > -1) ||
      (typeof mileage === "string")
    )
  })
}

/* ---- Local Types ---- */

/* * * * ItemData * * * */
/**
 * @typedef {Object} ItemData
 * @property {string|undefined} vin - VIN of vehicle (_AWG_)
 * @property {string|undefined} vIN - VIN of vehicle (_Elastic Search_)
 * @property {number|undefined} odometer Odometer reading (_AWG_)
 * @property {number|undefined} mileage Odometer reading (_Elastic Search_)
 * @property {string[] | undefined} imageFullUrls array of image URL strings (_AWG_)
 * @property {string|undefined} mainImage image url string (_Elastic Search_)
 * @property {boolean|undefined} hasImage (_Elastic Search_)
 */
/** @type {ItemData} */

/* * * * ItemPriceData * * * */
/**
 * @typedef {Object} ItemPriceData
 * @property {number} bidIncrement
 * @property {number} buyNow
 * @property {number} feedPrice
 * @property {number} highBid
 * @property {number} highOffer
 * @property {number} nextValidBidAmount
 * @property {number} outrightPrice
 * @property {number} startingBid
 */
/** @type {ItemPriceData} */

export {StratosLoaderData, StratosLoaderDummyData};